import clamp from "lodash/clamp"
import styled, { css } from "styled-components"
import type { CSSTextAlign } from "~/types/css"
import { Icon } from "~/ur-react-components"

const Layout = styled.div`
  position: relative;
`

interface StyledInputProps {
  showPlusMinusButtons: boolean
  textAlign?: CSSTextAlign
  hasError: boolean
  hasWarning: boolean
}

const StyledInput = styled.input.attrs({ type: "number" })<StyledInputProps>`
  height: 48px;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;

  border: 1px solid ${(props) => props.theme.colors.gray70};
  text-align: ${(props) => props.textAlign ?? "left"};
  border-radius: 4px;
  outline: none;

  ${(props) =>
    !props.showPlusMinusButtons &&
    css`
      padding-left: 1rem;
    `}

  -moz-appearance: textfield;
  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  ${(props) =>
    props.hasWarning &&
    css`
      border-color: ${props.theme.colors.warning40} !important;
    `}

  ${(props) =>
    props.hasError &&
    css`
      border-color: ${props.theme.colors.error40} !important;
    `}

  :focus {
    border: 2px solid;
    border-color: ${(props) => props.theme.colors.primary40};
  }
`

interface IconWrapperProps {
  align: "start" | "end"
}

const IconWrapper = styled.button<IconWrapperProps>`
  background: none;
  position: absolute;
  border: none;
  appearance: none;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  padding: 0.5rem 1rem;
  // Required to make the start icon displayable as the number input seems to have
  // an inherent attempt at this functionality
  z-index: 1;

  color: ${(props) => props.theme.colors.gray50};

  &:hover {
    color: ${(props) => props.theme.colors.gray30};
  }

  ${(props) =>
    props.align === "start" &&
    css`
      left: 0;
    `}
  ${(props) =>
    props.align === "end" &&
    css`
      right: 0;
    `};
`

const Error = styled.div`
  color: ${(props) => props.theme.colors.error40};
  font-size: 12px;
  margin-top: 8px;
  margin-left: 4px;

  position: absolute;
`

const Warning = styled.div`
  color: ${(props) => props.theme.colors.warning40};
  font-size: 12px;
  margin-top: 8px;
  margin-left: 4px;

  position: absolute;
`

export interface NumberInputProps {
  value: number | null
  minValue?: number
  maxValue?: number
  showPlusMinusButtons?: boolean
  onChange: (newValue: number) => void
  width?: string
  textAlign?: CSSTextAlign
  error?: string | null
  warning?: string | null
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  minValue: _minValue,
  maxValue: _maxValue,
  showPlusMinusButtons,
  width,
  textAlign,
  error,
  warning,
}) => {
  const maxValue = _maxValue ?? Number.POSITIVE_INFINITY
  const minValue = _minValue ?? Number.NEGATIVE_INFINITY

  return (
    <>
      <Layout>
        {showPlusMinusButtons && (
          <IconWrapper
            type="button"
            align="start"
            onClick={() => onChange(Math.max(minValue, (value ?? 0) - 1))}
          >
            <Icon icon="minus" />
          </IconWrapper>
        )}
        <StyledInput
          value={value ?? undefined}
          onChange={(e) => {
            const value = Number.parseInt(e.target.value, 10)
            onChange(clamp(Math.round(value), minValue, maxValue))
          }}
          width={width}
          textAlign={(textAlign ?? showPlusMinusButtons) ? "center" : undefined}
          showPlusMinusButtons={!!showPlusMinusButtons}
          hasError={!!error}
          hasWarning={!!warning}
        />
        {showPlusMinusButtons && (
          <IconWrapper
            type="button"
            align="end"
            onClick={() => {
              onChange(Math.min(maxValue, (value ?? 0) + 1))
            }}
          >
            <Icon icon="plus" />
          </IconWrapper>
        )}
      </Layout>
      {error && <Error>{error}</Error>}
      {warning && <Warning>{warning}</Warning>}
    </>
  )
}

export default NumberInput
